import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import type { PropertyInfoDto } from "@/features/upload/api";

// Simple admin table of the saved-property DB (the human-verified block/lot/BIN/
// CB/applicant that auto-fills future uploads). List + delete only — kept minimal.
export function SavedPropertiesPage() {
  const qc = useQueryClient();

  const rows = useQuery({
    queryKey: ["saved-properties"],
    queryFn: () => api<PropertyInfoDto[]>("/api/property-info/all"),
  });

  const del = useMutation({
    mutationFn: (id: number) => api<void>(`/api/property-info/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast.success("Deleted saved property");
      qc.invalidateQueries({ queryKey: ["saved-properties"] });
    },
    onError: () => toast.error("Couldn't delete — try again"),
  });

  const remove = (p: PropertyInfoDto) => {
    if (window.confirm(`Delete saved info for ${p.originalAddress}?`)) del.mutate(p.id);
  };

  return (
    <div className="animate-reveal">
      <div className="mb-8">
        <div className="label mb-2">Admin</div>
        <h1 className="text-[clamp(2.25rem,4.5vw,3.25rem)] font-medium tracking-tight text-ink">
          Saved Properties
        </h1>
        <p className="mt-2 text-[0.92rem] text-ink-muted">
          Verified property info reused to auto-fill future uploads. Delete a row to stop reusing it.
        </p>
      </div>

      <section className="rounded-lg border border-rule bg-surface shadow-soft">
        <header className="flex items-center justify-between border-b border-rule px-6 py-3.5">
          <div className="text-[0.95rem] font-semibold text-ink">
            {rows.data ? `${rows.data.length} saved` : "Saved records"}
          </div>
          {rows.isFetching && !rows.isLoading && (
            <div className="text-[0.82rem] text-ink-muted">Refreshing…</div>
          )}
        </header>

        {rows.isLoading ? (
          <div className="px-6 py-10 text-center text-[0.92rem] text-ink-muted">Loading…</div>
        ) : rows.isError ? (
          <div className="px-6 py-10 text-center text-[0.92rem] text-danger">
            Couldn't load saved properties.
          </div>
        ) : !rows.data || rows.data.length === 0 ? (
          <div className="px-6 py-10 text-center">
            <div className="label mb-1">Nothing saved yet</div>
            <p className="text-[0.92rem] text-ink-muted">
              Property info is saved when you generate a form from the Upload or PAA flow.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[0.92rem]">
              <thead className="bg-canvas-2 text-[0.78rem] font-semibold uppercase tracking-wider text-ink-muted">
                <tr>
                  <Th className="pl-6">Address</Th>
                  <Th>Block</Th>
                  <Th>Lot</Th>
                  <Th>BIN</Th>
                  <Th>CB</Th>
                  <Th>Applicant</Th>
                  <Th className="text-right">Used</Th>
                  <Th className="pr-6 text-right">Delete</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-rule">
                {rows.data.map((p) => (
                  <tr key={p.id} className="hover:bg-canvas-2/40">
                    <Td className="pl-6 text-ink">{p.originalAddress}</Td>
                    <Td className="tabular-nums text-ink-muted">{p.block || "—"}</Td>
                    <Td className="tabular-nums text-ink-muted">{p.lot || "—"}</Td>
                    <Td className="tabular-nums text-ink-muted">{p.bin || "—"}</Td>
                    <Td className="tabular-nums text-ink-muted">{p.cbNumber || "—"}</Td>
                    <Td className="text-ink-muted">{p.applicantName || "—"}</Td>
                    <Td className="text-right tabular-nums text-ink-muted">{p.usageCount}</Td>
                    <Td className="pr-6 text-right">
                      <Button
                        intent="soft"
                        size="sm"
                        onClick={() => remove(p)}
                        disabled={del.isPending}
                      >
                        Delete
                      </Button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={cn("px-3 py-2.5 text-left font-semibold", className)}>{children}</th>;
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn("px-3 py-2.5 align-middle", className)}>{children}</td>;
}
